import { Injectable } from '@angular/core';
import {
  catchError,
  concatMap,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  switchMap,
  take,
  throwError,
  withLatestFrom
} from 'rxjs';
import { BaseDomainModel } from '../../../models/base/base-domain-model';
import { WebSocketService } from '../../../services/web-socket.service';
import { Asset } from '../../../models/image/dto/asset';
import { AssetTranscodeStatusUpdate } from '../../../models/image/dto/asset-transcode-status-update';
import { MediaConvertJobStatus } from '../../../models/enum/dto/media-convert-job-status.enum';
import { WebSocketMessageContextDataUpdate } from '../../../models/web-sockets/contexts/web-socket-message-context-data-update';
import {
  MediaManagerAssetDeleteState,
  MediaManagerLibraryContext,
  MediaManagerLibraryKey,
  MediaManagerStore
} from '../store';
import {
  applyDeletedAssets,
  applyUpdatedAsset,
  applyUploadedAssetToInMemoryState,
  findAssetInLibrary,
  replaceAssetByIdInMemoryState
} from '../mutations';
import { isVideoUploadAwaitingTranscodeProgress } from '../upload';
import { exists } from '../../../functions/exists';
import { AssetRepository } from '../../../repositories/asset/asset-repository';
import { WebSocketReceiveAssetUpdated } from 'src/app/models/web-sockets/messages/receive/web-socket-receive-asset-updated';
import {
  MediaManagerMultiUploadItemState,
  MediaManagerMultiUploadState
} from 'src/app/models/media-manager/media-manager-multi-upload-state';
import {
  findBrandIdForAsset,
  findBrandIdForAssetUniqueId,
  resolveMediaManagerBrandIdWithActiveFallback
} from '../media-manager-brand-scope';
import { WebSocketAssetDeleted } from '../../../models/web-sockets/messages/receive/web-socket-asset-deleted';

/**
 * Reconciles remote asset-lifecycle websocket events (transcode progress + completion, upload, delete, update)
 * into the store's in-memory state. Every reconciliation path falls back to a full asset-library
 * refetch when the incremental patch cannot apply cleanly, so state drift self-heals within a single event.
 *
 * Bootstrapped eagerly by the logged-in scope so listeners are alive as soon as the scope activates.
 */
@Injectable() // Provided by Logged In Scope
export class MediaManagerRemoteAssetSyncService extends BaseDomainModel {
  constructor(
    private assetRepository: AssetRepository,
    private webSocketService: WebSocketService,
    private store: MediaManagerStore
  ) {
    super();
    this.setupRemoteAssetTranscodeStatusUpdateSyncSub();
    this.setupRemoteAssetUploadedSyncSub();
    this.setupRemoteAssetDeletedSyncSub();
    this.setupRemoteAssetUpdatedSyncSub();
  }

  /**
   * Fetch the finalized transcoded asset from the API, falling back to the websocket payload if the fetch
   * fails so a failed HTTP round-trip does not block the state reconciliation.
   */
  public getCompletedTranscodedAsset$(asset: Asset): Observable<Asset> {
    return this.assetRepository.getAsset$(asset.id, asset.md5Hash).pipe(
      catchError(err => {
        console.info('MediaManager could not fetch completed transcoded asset; using websocket asset payload.', err);
        return of(asset);
      })
    );
  }

  private setupRemoteAssetTranscodeStatusUpdateSyncSub(): void {
    this.remoteAssetTranscodeStatusUpdateSyncSub();
    this.setupRemoteAssetTranscodeCompleteSyncSub();
  }

  /**
   * Forward mid-transcode progress percentages into the active multi-upload batch so the UI progress bar keeps
   * moving. Only relevant while the user has an active upload session with matching items.
   */
  private remoteAssetTranscodeStatusUpdateSyncSub(): void {
    this.store.assetTranscodeUpdateMessages$
      .pipe(
        map(message => message?.payload),
        filter(update => exists(update)),
        withLatestFrom(this.store.multiFileUploadState$),
        filter(([update, uploadState]) => {
          return (
            update?.status === MediaConvertJobStatus.StatusUpdate &&
            uploadState.itemStates.some(item => {
              return (
                isVideoUploadAwaitingTranscodeProgress(item) &&
                this.isAssetTranscodeStatusUpdateForUploadItem(update, item)
              );
            })
          );
        })
      )
      .subscribeWhileAlive({
        owner: this,
        next: ([update, uploadState]) => {
          uploadState.itemStates
            .filter(item => {
              return (
                isVideoUploadAwaitingTranscodeProgress(item) &&
                this.isAssetTranscodeStatusUpdateForUploadItem(update, item)
              );
            })
            .forEach(item => {
              this.store.connectToMultiUploadEvent({
                type: 'FileTranscodeProgressUpdated',
                scope: 'item',
                file: item.file,
                percentComplete: update?.jobPercentComplete ?? 0
              });
            });
        }
      });
  }

  /**
   * Independent of the active upload flow, a transcode completion means an in-library placeholder asset
   * (outputProcessed === false) now has a fetchable output. Refetch the finalized asset and patch it into
   * state so its preview loads, even if the upload modal was closed or the upload listener has already torn
   * down.
   */
  private setupRemoteAssetTranscodeCompleteSyncSub(): void {
    this.store.assetTranscodeUpdateMessages$
      .pipe(
        map(message => message?.payload),
        filter(update => exists(update)),
        filter(update => update?.completed() && update?.hasAssetWithUniqueId()),
        withLatestFrom(this.store.assetLibrariesByBrandId$),
        filter(([update, assetLibrariesByBrandId]) => {
          const libraryKey = findBrandIdForAsset(update.asset, assetLibrariesByBrandId);
          return exists(libraryKey) && assetLibrariesByBrandId.has(libraryKey);
        }),
        concatMap(([update]) => {
          return this.getCompletedTranscodedAsset$(update.asset).pipe(
            concatMap(completedAsset => this.applyRemoteAssetTranscodeCompletedAsset$(completedAsset)),
            catchError(err => {
              console.info(
                `MediaManager could not reconcile completed transcoded asset from websocket: ${
                  err?.message || err?.error?.message || err
                }`
              );
              return of(null);
            })
          );
        })
      )
      .subscribeWhileAlive({ owner: this });
  }

  private applyRemoteAssetTranscodeCompletedAsset$(completedAsset: Asset): Observable<Asset | null> {
    return this.getLibraryContextForAsset$(completedAsset, 'remote asset transcode completed').pipe(
      switchMap(({ brandId, filesystem, assetLibrary }) => {
        // Transcoding can change the asset's md5Hash (input vs output), so the placeholder is matched by
        // asset id and replaced with the finalized asset rather than patched by composite unique id.
        const inMemoryPatchSucceeded = replaceAssetByIdInMemoryState(completedAsset, filesystem, assetLibrary);
        if (!inMemoryPatchSucceeded) {
          return this.store.refreshBrandAssetLibrary$(brandId).pipe(map(() => completedAsset));
        }
        this.store.notifyFilesystemChanged('remote asset transcode completed');
        return of(completedAsset);
      })
    );
  }

  private setupRemoteAssetUploadedSyncSub(): void {
    const remoteAssetUploadedSyncSub = this.store.assetUploadedMessages$
      .pipe(
        map(message => message?.payload),
        filter(asset => exists(asset?.getUniqueIdentifier())),
        concatMap(asset => {
          return this.applyRemoteUploadedAsset$(asset).pipe(
            catchError(err => {
              console.info(
                `MediaManager could not reconcile uploaded asset from websocket: ${
                  err?.message || err?.error?.message || err
                }`
              );
              return of(null);
            })
          );
        })
      )
      .subscribe();
    this.pushSub(remoteAssetUploadedSyncSub);
  }

  private setupRemoteAssetDeletedSyncSub(): void {
    const remoteAssetDeletedSyncSub = this.store.assetDeletedMessages$
      .pipe(
        filter(message => exists(message?.getAssetUniqueId())),
        concatMap(message => {
          return this.applyRemoteAssetDeleted$(message).pipe(
            catchError(err => {
              console.info(
                `MediaManager could not reconcile deleted asset from websocket: ${
                  err?.message || err?.error?.message || err
                }`
              );
              return of(null);
            })
          );
        })
      )
      .subscribe();
    this.pushSub(remoteAssetDeletedSyncSub);
  }

  private setupRemoteAssetUpdatedSyncSub(): void {
    const remoteAssetUpdatedSyncSub = this.webSocketService
      .getRelevantMessage$<WebSocketReceiveAssetUpdated>(WebSocketMessageContextDataUpdate.AssetUpdated)
      .pipe(
        map(message => message?.payload),
        filter(updatedAsset => exists(updatedAsset?.getUniqueIdentifier())),
        concatMap(updatedAsset => {
          return this.applyRemoteAssetUpdated$(updatedAsset).pipe(
            catchError(err => {
              console.info(
                `MediaManager could not reconcile updated asset from websocket: ${
                  err?.message || err?.error?.message || err
                }`
              );
              return of(null);
            })
          );
        })
      )
      .subscribe();
    this.pushSub(remoteAssetUpdatedSyncSub);
  }

  private applyRemoteAssetDeleted$(message: WebSocketAssetDeleted): Observable<string | null> {
    const assetUniqueId = message.getAssetUniqueId()?.trim();
    if (!assetUniqueId) {
      return of(null);
    }
    return this.getLibraryContextForAssetUniqueId$(assetUniqueId, message.payload, 'remote asset deleted').pipe(
      switchMap(({ brandId, filesystem, assetLibrary }) => {
        return this.store.assetDeleteStates$.pipe(
          take(1),
          switchMap(deleteStates => {
            if (this.isDeletedAssetFromLocalDelete(assetUniqueId, deleteStates)) {
              this.store.debug.logRemoteAssetDeleteSkippedForLocalDelete(
                this.store.debug.getAssetNameForDebugLog(assetUniqueId, assetLibrary)
              );
              return of(assetUniqueId);
            }
            if (!filesystem || !assetLibrary) {
              return this.refetchAssetLibraryAfterRemoteAssetDelete$(assetUniqueId, brandId);
            }
            if (!findAssetInLibrary(assetUniqueId, assetLibrary)) {
              return of(assetUniqueId);
            }
            const inMemoryPatchSucceeded = applyDeletedAssets([assetUniqueId], filesystem, assetLibrary);
            if (!inMemoryPatchSucceeded) {
              return this.refetchAssetLibraryAfterRemoteAssetDelete$(assetUniqueId, brandId);
            }
            this.store.notifyFilesystemChanged('remote asset deleted');
            return of(assetUniqueId);
          })
        );
      })
    );
  }

  private isDeletedAssetFromLocalDelete(
    assetUniqueId: string,
    deleteStates: Map<string, MediaManagerAssetDeleteState>
  ): boolean {
    return deleteStates.has(assetUniqueId);
  }

  private refetchAssetLibraryAfterRemoteAssetDelete$(assetUniqueId: string, brandId: string): Observable<string> {
    return this.store.refreshBrandAssetLibrary$(brandId).pipe(map(() => assetUniqueId));
  }

  private applyRemoteAssetUpdated$(updatedAsset: Asset): Observable<Asset> {
    return this.getLibraryContextForAsset$(updatedAsset, 'remote asset updated').pipe(
      switchMap(({ brandId, filesystem, assetLibrary }) => {
        const assetUniqueId = updatedAsset.getUniqueIdentifier();
        const currentAsset = findAssetInLibrary(assetUniqueId, assetLibrary);
        if (!currentAsset) {
          return this.refetchAssetLibraryAfterRemoteAssetUpdated$(updatedAsset, brandId);
        }
        const inMemoryPatchSucceeded = applyUpdatedAsset(currentAsset, updatedAsset, filesystem);
        if (!inMemoryPatchSucceeded) {
          return this.refetchAssetLibraryAfterRemoteAssetUpdated$(updatedAsset, brandId);
        }
        this.store.notifyFilesystemChanged('remote asset updated');
        return of(updatedAsset);
      })
    );
  }

  private refetchAssetLibraryAfterRemoteAssetUpdated$(updatedAsset: Asset, brandId: string): Observable<Asset> {
    return this.store.refreshBrandAssetLibrary$(brandId).pipe(map(() => updatedAsset));
  }

  private applyRemoteUploadedAsset$(uploadedAsset: Asset): Observable<Asset | null> {
    return this.getLibraryContextForAsset$(uploadedAsset, 'remote asset uploaded').pipe(
      switchMap(({ brandId, filesystem, assetLibrary }) => {
        return this.store.multiFileUploadState$.pipe(
          take(1),
          switchMap(multiUploadState => {
            if (
              this.isUploadedAssetFromActiveLocalUpload(uploadedAsset, multiUploadState) &&
              uploadedAsset?.outputProcessed
            ) {
              return of(uploadedAsset);
            }
            if (!filesystem || !assetLibrary) {
              return this.refetchAssetLibraryAfterRemoteUpload$(uploadedAsset, brandId);
            }
            const inMemoryPatchSucceeded = applyUploadedAssetToInMemoryState(uploadedAsset, filesystem, assetLibrary);
            if (!inMemoryPatchSucceeded) {
              return this.refetchAssetLibraryAfterRemoteUpload$(uploadedAsset, brandId);
            }
            this.store.notifyFilesystemChanged('remote asset uploaded');
            return of(uploadedAsset);
          })
        );
      })
    );
  }

  private isUploadedAssetFromActiveLocalUpload(
    uploadedAsset: Asset,
    multiUploadState: MediaManagerMultiUploadState
  ): boolean {
    return this.uploadedAssetMatchesMultiUploadItem(uploadedAsset, multiUploadState);
  }

  private refetchAssetLibraryAfterRemoteUpload$(uploadedAsset: Asset, brandId: string): Observable<Asset> {
    return this.store.refreshBrandAssetLibrary$(brandId).pipe(map(() => uploadedAsset));
  }

  private uploadedAssetMatchesMultiUploadItem(
    uploadedAsset: Asset,
    multiUploadState: MediaManagerMultiUploadState
  ): boolean {
    const uploadedFileName = uploadedAsset?.fileName?.trim().toLowerCase();
    if (!uploadedFileName) return false;
    return multiUploadState.itemStates.some(item => {
      return item.fileName?.trim().toLowerCase() === uploadedFileName;
    });
  }

  /** True when the WS transcode-status update targets one of the items in an active upload batch. */
  public isAssetTranscodeStatusUpdateForUploadItem(
    update: AssetTranscodeStatusUpdate,
    item: MediaManagerMultiUploadItemState
  ): boolean {
    const req = item.processingContext?.normalizedReq;
    const requestLibraryKey = req?.metadata?.get('CompanyId')?.trim().toLowerCase();
    const updateLibraryKey = update?.getTranscodeMetadataValue('CompanyId');
    if (!req || !requestLibraryKey || !updateLibraryKey || requestLibraryKey !== updateLibraryKey) {
      return false;
    }
    return update?.isFor(req);
  }

  private getLibraryContextForAsset$(asset: Asset, reason: string): Observable<MediaManagerLibraryContext> {
    return this.store.assetLibrariesByBrandId$.pipe(
      take(1),
      switchMap(assetLibrariesByBrandId => {
        const resolvedBrandId = findBrandIdForAsset(asset, assetLibrariesByBrandId);
        if (!resolvedBrandId || !assetLibrariesByBrandId.has(resolvedBrandId)) {
          return EMPTY;
        }
        return this.getLibraryContextForResolvedBrand$(resolvedBrandId, reason);
      })
    );
  }

  private getLibraryContextForAssetUniqueId$(
    assetUniqueId: string,
    asset: Asset | null | undefined,
    reason: string
  ): Observable<MediaManagerLibraryContext> {
    return this.store.assetLibrariesByBrandId$.pipe(
      take(1),
      switchMap(assetLibrariesByBrandId => {
        const resolvedBrandId =
          findBrandIdForAsset(asset, assetLibrariesByBrandId) ||
          findBrandIdForAssetUniqueId(assetUniqueId, assetLibrariesByBrandId);
        if (!resolvedBrandId || !assetLibrariesByBrandId.has(resolvedBrandId)) {
          return EMPTY;
        }
        return this.getLibraryContextForResolvedBrand$(resolvedBrandId, reason);
      })
    );
  }

  private getLibraryContextForResolvedBrand$(
    resolvedBrandId: MediaManagerLibraryKey | null,
    reason: string
  ): Observable<MediaManagerLibraryContext> {
    const brandId = resolveMediaManagerBrandIdWithActiveFallback(resolvedBrandId, null, reason);
    if (!brandId) {
      return throwError(() => new Error(`MediaManager could not resolve brand for '${reason}'.`));
    }
    return this.store.getBrandLibraryContext$(brandId);
  }
}
